/**
 * FomoHud.ts — the picture-bonus FOMO meter.
 *
 * A purple "Picture Bonus" meter that fills as colors are completed and teases
 * the projected bonus you'd leave on the table by stopping early. The closer to
 * complete, the brighter/pulsier (coverage curve is (done/K)^1.5 -> the last
 * colors matter most, which the copy emphasises).
 */
import { Container, Graphics } from 'pixi.js';
import { makeText } from './widgets';
import theme, { hex } from '../render/theme';
import { money } from '../util/format';
import { Tweens } from '../util/tween';
import { easeOutCubic } from '../util/ease';
import type { Viewport } from '../app/AppContext';

export class FomoHud extends Container {
  private panel = new Graphics();
  private track = new Graphics();
  private fill = new Graphics();
  private starIcon = new Graphics();
  private title;
  private teaser;
  private remainingText;

  private w = 0;
  private coverage = 0; // (done/K)^1.5 visualized
  private pulse = 0;

  constructor() {
    super();
    this.title = makeText('Picture Bonus', { size: 13, weight: '800', color: theme.color.white });
    this.teaser = makeText('', { size: 15, weight: '800', color: hex(theme.palette.gold1) });
    this.remainingText = makeText('', { size: 11, weight: '700', color: hex('#e9d9ff') });
    this.addChild(this.panel, this.starIcon, this.title, this.track, this.fill, this.teaser, this.remainingText);
  }

  /** done/total colors and the projected teaser amount. */
  update(done: number, total: number, teaser: number): void {
    const cov = Math.pow(Math.max(0, done) / Math.max(1, total), 1.5);
    Tweens.to({
      from: this.coverage,
      to: cov,
      duration: theme.motion.slow,
      ease: easeOutCubic,
      onUpdate: (v) => {
        this.coverage = v;
        this.redraw();
      },
    });
    const remaining = total - done;
    this.teaser.text = remaining > 0 ? '~' + money(teaser) : 'Locked in!';
    this.remainingText.text =
      remaining > 0
        ? `${remaining} ${remaining === 1 ? 'color' : 'colors'} to unlock the full bonus`
        : 'All colors complete — bonus armed';
    this.layoutTexts();
  }

  tick(dtMs: number): void {
    // gentle pulse that intensifies near completion
    this.pulse += dtMs * 0.004;
    const glow = 0.5 + 0.5 * Math.sin(this.pulse);
    const intensity = this.coverage; // 0..1
    this.fill.alpha = 0.85 + glow * 0.15 * intensity;
  }

  layout(v: Viewport, y: number): void {
    const pad = 14;
    this.w = v.width - pad * 2;
    this.position.set(pad, y);
    this.redraw();
    this.layoutTexts();
  }

  private redraw(): void {
    const w = this.w;
    const h = 50;
    this.panel.clear();
    this.panel.roundRect(0, 0, w, h, theme.radius.card).fill(hex(theme.palette.purpleDeep));
    this.panel.roundRect(0, 0, w, h, theme.radius.card).fill({ color: hex(theme.palette.purple), alpha: 0.5 });
    this.panel.roundRect(0, 0, w, h, theme.radius.card).stroke({ width: 1.5, color: hex(theme.palette.purple), alpha: 0.6 });

    // star icon
    this.starIcon.clear();
    drawStar(this.starIcon, 11, hex(theme.palette.gold1));
    this.starIcon.position.set(24, 18);

    // meter — anchored at the bottom of the panel, clear of the title/subtitle stack above
    const mx = 14;
    const my = 39;
    const mw = w - 28;
    const mh = 7;
    this.track.clear();
    this.track.roundRect(mx, my, mw, mh, mh / 2).fill({ color: hex('#2a1352'), alpha: 0.9 });
    this.fill.clear();
    const fw = Math.max(0, mw * this.coverage);
    if (fw > 2) {
      this.fill.roundRect(mx, my, fw, mh, mh / 2).fill(hex(theme.palette.gold2));
      this.fill.roundRect(mx, my, fw, mh, mh / 2).fill({ color: hex(theme.palette.gold1), alpha: 0.55 });
    }
  }

  private layoutTexts(): void {
    const w = this.w;
    this.title.position.set(42, 7);
    this.teaser.anchor.set(1, 0);
    this.teaser.position.set(w - 14, 6);
    this.remainingText.position.set(42, 23);
  }
}

function drawStar(g: Graphics, r: number, color: number): void {
  const spikes = 5;
  const inner = r * 0.45;
  for (let i = 0; i < spikes * 2; i++) {
    const rad = i % 2 === 0 ? r : inner;
    const a = (i / (spikes * 2)) * Math.PI * 2 - Math.PI / 2;
    const x = Math.cos(a) * rad;
    const y = Math.sin(a) * rad;
    if (i === 0) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  g.closePath().fill({ color });
}
